import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BlobWithFilename, Tab } from '../../../../shared/models';
import { Store, select } from '@ngrx/store';
import { AppState } from '../../../../app.reducers';
import { Observable } from 'rxjs';
import { Workspace } from '../../../shared/models/workspace';
import { getAdminWorkspace, getAdminEditPortfolioListTab } from '../../admin-store/admin.selectors';
import { LoadWorkspaceRolesAction } from '../../../workspace/workspace-store';
import {
  ClearAdminPortfolioAction,
  DeleteAdminPortfolioLogoAction,
  LoadAdminPortfolioAction,
  LoadAdminPortfoliosAction,
  ReassignAdminPropertyAction,
  RefreshPortfolioAnalyticsAction,
  ResendAdminPortfolioInviteAction,
  RevokeAdminPortfolioInviteAction,
  SaveAdminPortfolioInviteAction,
  SetAdminEditPortfolioTabAction,
  ToggleAdminPropertyActiveStatusAction,
  ToggleAdminUserActiveStatusAction,
  UpdateAdminPortfolioAction,
  UpdateAdminWorkspaceUserAction,
  DuplicateAdminPropertyAction,
  ClearAdminPropertyCacheAction
} from '../../admin-store';
import { ToastrService } from 'ngx-toastr';
import { UserProfile } from '../../../user/models';
import { getUserProfile } from '../../../user/redux/user.selectors';
import { SubSink } from 'subsink';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { distinctUntilChanged, take } from 'rxjs/operators';
import { ModalComponent, WorkspaceProperty, WorkspaceUser } from 'app/internal/shared';
import { getRolesForRoleDropdown } from 'app/internal/workspace/workspace-store/workspace.selectors';
import { Invitation, Role } from 'app/internal/workspace/shared/models';
import { AdminPortfolioPropertiesComponent } from '../../components/portfolios/admin-edit-portfolio-properties';
import { ReportsService } from 'app/internal/reports/shared/services';
import * as FileSaver from 'file-saver-es';
import { WorkspaceUsersListDisplayComponent } from 'app/internal/shared/components/workspace-users-list-display/workspace-users-list-display.component';
import { WorkspaceInvitesListDisplayComponent } from 'app/internal/shared/components/workspace-invites-list-display/workspace-invites-list-display.component';

@Component({
  templateUrl: './admin-portfolio.component.html',
  styleUrls: ['./admin-portfolio.component.scss'],
  standalone: false
})
export class AdminPortfolioComponent implements OnInit, OnDestroy {

  @ViewChild('adminEditPortfolioProperties', { static: true }) public adminEditPortfolioProperties: AdminPortfolioPropertiesComponent;
  @ViewChild('adminEditPortfolioUsers', { static: true }) public adminEditPortfolioUsers: WorkspaceUsersListDisplayComponent;
  @ViewChild('adminPortfolioInvites', { static: true }) public adminPortfolioInvites: WorkspaceInvitesListDisplayComponent;

  @ViewChild('resendModal', { static: true }) public resendModal: ModalComponent;
  @ViewChild('revokeModal', { static: true }) public revokeModal: ModalComponent;
  @ViewChild('viewLinkModal', { static: true }) public viewLinkModal: ModalComponent;

  workspace$: Observable<Workspace>;
  userProfile$: Observable<UserProfile>;
  roles$: Observable<Role[]>;
  roles: Role[];
  invitation: Invitation;
  activeTabIndex = 0;
  workspaceId: string;
  companyName: string;
  workspaceName: string;
  workspaceProperties: WorkspaceProperty[];
  workspaceUsers: WorkspaceUser[];
  workspaceInvites: Invitation[];
  tabs: Tab[];
  currentTab: Tab;
  selectedTab$: Observable<string>;
  readonly tabGeneral = 'General';
  readonly tabProperties = 'Properties';
  readonly tabUsers = 'Users';
  readonly tabInvites = 'Invites';

  private subs = new SubSink();

  isLoading: boolean = true;
  invitationUrl: string = '';
  loadIntegrationDetail: boolean = false;

  constructor(private store: Store<AppState>,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private reportsService: ReportsService,
    private router: Router) {
    this.workspace$ = this.store.pipe(select(getAdminWorkspace));
    this.userProfile$ = this.store.pipe(select(getUserProfile));
    this.selectedTab$ = this.store.pipe(select(getAdminEditPortfolioListTab));
    this.roles$ = this.store.pipe(select(getRolesForRoleDropdown));
  }

  ngOnInit() {
    this.route.params.subscribe(
      (params: Params) => {
        this.workspaceId = params['workspaceId'];
        this.store.dispatch(new LoadAdminPortfoliosAction());
        this.store.dispatch(new LoadAdminPortfolioAction({ workspaceId: this.workspaceId, includeIntegrationDetail: this.loadIntegrationDetail }));
        this.store.dispatch(new LoadWorkspaceRolesAction());
      }
    );

    this.subs.sink = this.workspace$.subscribe(workspace => {
      this.companyName = workspace?.companyName;
      this.workspaceName = workspace?.name;
      this.workspaceProperties = workspace?.workspaceProperties;
      this.workspaceUsers = workspace?.workspaceUsers;
      this.workspaceInvites = workspace?.workspaceInvites;
    });

    // Set tabs & selected tab
    this.tabs = this.getTabsData();
    this.setSelectedTab();

    // Updated the selected tab, if we have one
    this.subs.sink = this.selectedTab$
      .pipe(distinctUntilChanged())
      .subscribe((selectedTab: string) => {

        if (selectedTab == null || selectedTab === '') {
          return;
        }

        const index = this.tabs.findIndex(x => x.name === selectedTab);
        if (index && index !== -1) {
          this.setSelectedTab(this.tabs[index]);
        }
      });

    // select tab from route param (if any)
    this.subs.sink = this.route.queryParams.pipe(take(1))
      .subscribe(params => {
        const tabIndex = params['tabIndex'];
        if (tabIndex) {
          this.setSelectedTab(this.tabs[tabIndex]);
        }

        //if there is an "integration" param, then we need to load the integration details
        const integration = params['integration'];
        if (integration) {
          this.loadIntegrationDetail = true;
          this.store.dispatch(new LoadAdminPortfolioAction({ workspaceId: this.workspaceId, includeIntegrationDetail: this.loadIntegrationDetail }));
        }
      });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.store.dispatch(new SetAdminEditPortfolioTabAction(this.tabs[0].name)); // Always reset the Tab back to the first tab
    this.store.dispatch(new ClearAdminPortfolioAction());
  }

  onDeleteLogo(isHeaderLogo: boolean) {
    this.store.dispatch(new DeleteAdminPortfolioLogoAction({ isHeaderLogo: isHeaderLogo, workspaceId: this.workspaceId }));
  }

  onSave(settings: Workspace) {
    this.store.dispatch(new UpdateAdminPortfolioAction(settings));
  }

  onSaveLogo() {
    this.toastr.success('Logo Saved');
    this.store.dispatch(new LoadAdminPortfolioAction({ workspaceId: this.workspaceId, includeIntegrationDetail: this.loadIntegrationDetail }));
  }

  onUpdateRole(user: WorkspaceUser) {
    // Updating roleName to selected value
    this.store.dispatch(new UpdateAdminWorkspaceUserAction({ workSpaceUser: user, workSpaceId: this.workspaceId }));
  }

  onInviteUser(invite: Invitation) {
    this.store.dispatch(new SaveAdminPortfolioInviteAction({ invite: invite, workSpaceId: this.workspaceId }));
  }

  onConfirmResend(invite: Invitation) {
    this.invitation = invite;
    this.resendModal.show();
  }

  onConfirmRevoke(invite: Invitation) {
    this.invitation = invite;
    this.revokeModal.show();
  }

  onConfirmViewLink(invite: Invitation) {
    this.invitation = invite;
    this.invitationUrl = window.location.origin + '/#/join/invite/' + invite.invitationCode;
    this.viewLinkModal.show();
  }

  onResend() {
    this.store.dispatch(new ResendAdminPortfolioInviteAction({ workspaceUserInvitationId: this.invitation.workspaceUserInvitationId, workSpaceId: this.workspaceId }));
  }

  onRevoke() {
    this.store.dispatch(new RevokeAdminPortfolioInviteAction({ workspaceUserInvitationId: this.invitation.workspaceUserInvitationId, workSpaceId: this.workspaceId }));
  }

  onViewLink() {
    navigator.clipboard.writeText(this.invitationUrl);
  }

  onTabClick(selectedTab: Tab) {
    if (this.shouldChangeSelectedTab(selectedTab)) {
      this.store.dispatch(new SetAdminEditPortfolioTabAction(selectedTab.name));
      this.setSelectedTab(selectedTab);

      // There's an issue w/ the DxGrid where its not completely initialized correctly when its
      // in a tab that is initially hidden.  This will simply refresh/rebuild the DxGrid.
      if (selectedTab.name === this.tabProperties) {
        this.adminEditPortfolioProperties.rebuildDxGrid();
      } else if (selectedTab.name === this.tabUsers) {
        this.adminEditPortfolioUsers.rebuildDxGrid();
      } else if (selectedTab.name === this.tabInvites) {
        this.adminPortfolioInvites.rebuildDxGrid();
      }
    }
  }

  onDeactivateProperty(propertyId: string) {
    this.store.dispatch(new ToggleAdminPropertyActiveStatusAction({ propertyId: propertyId, isActive: false, workspaceId: this.workspaceId }));
  }

  onReactivateProperty(propertyId: string) {
    this.store.dispatch(new ToggleAdminPropertyActiveStatusAction({ propertyId: propertyId, isActive: true, workspaceId: this.workspaceId }));
  }

  onReassignProperty(data: any) {
    this.store.dispatch(new ReassignAdminPropertyAction({ propertyId: data.propertyId, destinationWorkspaceId: data.destinationWorkspaceId, includeUsers: data.includeUsers }));
  }

  onDuplicateProperty(propertyId: string) {
    this.store.dispatch(new DuplicateAdminPropertyAction({ propertyId: propertyId }));
  }

  onClearCache(propertyId: string) {
    this.store.dispatch(new ClearAdminPropertyCacheAction(propertyId));
  }

  onDeactivateUser(userId: string) {
    this.store.dispatch(new ToggleAdminUserActiveStatusAction({ userId: userId, isActive: false, workspaceId: this.workspaceId }));
  }

  onReactivateUser(userId: string) {
    this.store.dispatch(new ToggleAdminUserActiveStatusAction({ userId: userId, isActive: true, workspaceId: this.workspaceId }));
  }

  onEditUser(userId) {
    this.router.navigate(['/useraccount/admin/portfolios/' + this.workspaceId + '/edit-user/' + userId]);
  }

  onExportWorkspaceComponents(workspaceId) {
    if (workspaceId) {
      // I'm bypassing all the NgRx stuff here, as we just need to get the CSV w/out saving anything in State, etc.
      this.subs.sink = this.reportsService.getWorkspaceComponentInventoryReportCsv(workspaceId).subscribe((csv: BlobWithFilename) => {
        FileSaver.saveAs(csv, csv.filename);
      });
    }
  }

  onRefreshPortfolioAnalytics(workspaceId) {
    this.store.dispatch(new RefreshPortfolioAnalyticsAction(workspaceId));
  }

  generalTabLoaded() {
    this.isLoading = false;
  }

  private getTabsData() {
    return <Tab[]>[
      {
        name: this.tabGeneral,
        title: 'General',
        isActive: this.isActiveTab(this.tabGeneral)
      },
      {
        name: this.tabProperties,
        title: 'Properties',
        isActive: this.isActiveTab(this.tabProperties)
      },
      {
        name: this.tabUsers,
        title: 'Users',
        isActive: this.isActiveTab(this.tabUsers)
      },
      {
        name: this.tabInvites,
        title: 'Invites',
        isActive: this.isActiveTab(this.tabInvites)
      }
    ];
  }

  private isActiveTab(currentTab?: string) {
    return (this.currentTab && currentTab === this.currentTab.name);
  }

  private setSelectedTab(selectedTab?: Tab) {

    // If a tab is passed in get its index, else default to 0
    const index = (selectedTab)
      ? this.tabs.findIndex(x => x.name === selectedTab.name)
      : 0;

    // Update all tabs to not be active
    this.tabs.map(x => x.isActive = false);

    // Set the selected tab
    this.tabs[index].isActive = true;
    this.currentTab = this.tabs[index];
  }

  private shouldChangeSelectedTab(selectedTab: Tab) {
    return selectedTab.name !== this.currentTab.name;
  }
}
